/**
 * MantineProvider theme override built from @commander/tokens.
 *
 * Targets the Mantine 9 theme API. Acts as the tokens-driven base that the
 * still-live `adminTheme`/`kioskTheme` (from `@commander/ui`) merge on top of
 * via `mergeThemeOverrides(commanderTheme, sharedTheme, …)`. Future
 * per-page agents will gradually retire the legacy Carbon-flavoured
 * `sharedTheme` so this becomes the sole source.
 *
 * Mantine 9 still does not accept CSS custom properties inside the `colors`
 * tuples (it wants concrete hex strings to derive shade arrays). Where the
 * theme can read a CSS var directly (fonts, radii, headings), we forward to
 * the variable so a token change in `tokens.css` flows through without a
 * code change. Where it cannot, we duplicate the hex literal from
 * `tokens.ts` — keep these in sync.
 */

import type { MantineThemeOverride } from '@mantine/core';
import { fontWeight, status } from './tokens';

type MantineColorsTuple = readonly [
  string, string, string, string, string,
  string, string, string, string, string,
];

function hexChannel(hex: string, offset: number): number {
  return parseInt(hex.slice(offset, offset + 2), 16);
}

function mixHex(a: string, b: string, ratio: number): string {
  const channels = [1, 3, 5].map((offset) => {
    const av = hexChannel(a, offset);
    const bv = hexChannel(b, offset);
    return Math.round(av * (1 - ratio) + bv * ratio);
  });
  return `#${channels.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Build a 10-shade Mantine colour tuple from a single accent hex.
 *
 * Mantine 9 still requires a 10-tuple for each colour family. Our tokens
 * model statuses as a single accent + a tinted background; this helper
 * expands an accent into a usable shade scale by mixing it with white/black
 * at fixed ratios. It is intentionally light-touch — Mantine's primary
 * colour usage in our app only really touches shades 5-7.
 *
 * Indices 0-5 mix toward white (lightest first); index 6 is the pure
 * accent; indices 7-9 mix toward black.
 */
function shadeRamp(accent: string): MantineColorsTuple {
  const mixToWhite = (ratio: number) => mixHex(accent, '#FFFFFF', ratio);
  const mixToBlack = (ratio: number) => mixHex(accent, '#000000', ratio);
  return [
    mixToWhite(0.92),
    mixToWhite(0.82),
    mixToWhite(0.68),
    mixToWhite(0.52),
    mixToWhite(0.34),
    mixToWhite(0.18),
    accent,
    mixToBlack(0.12),
    mixToBlack(0.24),
    mixToBlack(0.36),
  ];
}

/**
 * Commander theme — tokens-driven Mantine 9 theme override.
 *
 * Pass to `<MantineProvider theme={commanderTheme}>` directly for the
 * tokens-only surface, or merge with `adminTheme`/`kioskTheme` (which
 * already spread this base via `mergeThemeOverrides`) for the live SaaS
 * dashboard / kiosk shells.
 */
export const commanderTheme: MantineThemeOverride = {
  // CSS-var-friendly fields: the Mantine SDK passes these straight through
  // so a tokens.css change propagates without rebuilding the theme object.
  fontFamily: 'var(--font-sans)',
  fontFamilyMonospace: 'var(--font-mono)',

  primaryColor: 'commanderBlue',
  primaryShade: 6,

  // Colour ramps — Mantine 9 requires concrete hex tuples here. Source
  // accents from the status token family in tokens.ts.
  colors: {
    commanderBlue: shadeRamp(status.info.accent),
    commanderGreen: shadeRamp(status.online.accent),
    commanderRed: shadeRamp(status.error.accent),
    commanderYellow: shadeRamp(status.warning.accent),
    commanderGray: shadeRamp(status.neutral.accent),
  },

  headings: {
    fontFamily: 'var(--font-sans)',
    fontWeight: String(fontWeight.semibold),
    sizes: {
      h1: { fontSize: 'var(--font-2xl)', lineHeight: 'var(--line-tight)' },
      h2: { fontSize: 'var(--font-xl)', lineHeight: 'var(--line-tight)' },
      h3: { fontSize: 'var(--font-lg)', lineHeight: 'var(--line-tight)' },
      h4: { fontSize: 'var(--font-md)', lineHeight: 'var(--line-normal)' },
    },
  },

  defaultRadius: 'md',

  radius: {
    xs: '2px',
    sm: 'var(--radius-sm)',
    md: 'var(--radius-md)',
    lg: 'var(--radius-lg)',
    xl: '16px',
  },

  spacing: {
    xs: 'var(--space-1)',
    sm: 'var(--space-2)',
    md: 'var(--space-4)',
    lg: 'var(--space-6)',
    xl: 'var(--space-8)',
  },
};
